import type { CollectionItem } from "../api/types";
import { rarityRankOf } from "../catalog/rarity";
import { normalizeSearchText } from "../search/search-query";

export const COLLECTION_UI_SORTS = [
  "collector_asc",
  "collector_desc",
  "name_asc",
  "name_desc",
  "year_desc",
  "year_asc",
  "rare_first",
  "quantity_desc",
] as const;

export type CollectionUiSort = (typeof COLLECTION_UI_SORTS)[number];

export const COLLECTION_DEFAULT_SORT: CollectionUiSort = "collector_asc";

export const COLLECTION_SORT_OPTIONS: Array<{
  value: CollectionUiSort;
  label: string;
  description: string;
}> = [
  {
    value: "collector_asc",
    label: "Номер ↑",
    description: "Номер коллекционера по возрастанию",
  },
  {
    value: "collector_desc",
    label: "Номер ↓",
    description: "Номер коллекционера по убыванию",
  },
  { value: "name_asc", label: "А–Я", description: "Название А–Я" },
  { value: "name_desc", label: "Я–А", description: "Название Я–А" },
  { value: "year_desc", label: "Год ↓", description: "Новые годы сначала" },
  { value: "year_asc", label: "Год ↑", description: "Старые годы сначала" },
  { value: "rare_first", label: "Редкие", description: "Редкие сначала" },
  {
    value: "quantity_desc",
    label: "Кол-во ↓",
    description: "Количество экземпляров по убыванию",
  },
];

function compareName(a: string, b: string): number {
  const byNormalized = normalizeSearchText(a).localeCompare(
    normalizeSearchText(b),
    "en",
  );
  if (byNormalized !== 0) {
    return byNormalized;
  }
  return a.localeCompare(b, "en");
}

function compareCollectorNullsLast(
  a: number | null,
  b: number | null,
  desc: boolean,
): number {
  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  return desc ? b - a : a - b;
}

function compareReleaseId(a: string, b: string): number {
  return a.localeCompare(b, "en");
}

function compareCollectorThenStable(a: CollectionItem, b: CollectionItem): number {
  const collectorCmp = compareCollectorNullsLast(
    a.release.collectorNumber,
    b.release.collectorNumber,
    false,
  );
  if (collectorCmp !== 0) {
    return collectorCmp;
  }
  const byYear = b.release.year - a.release.year;
  if (byYear !== 0) {
    return byYear;
  }
  const byName = compareName(a.release.casting.name, b.release.casting.name);
  if (byName !== 0) {
    return byName;
  }
  return compareReleaseId(a.releaseId, b.releaseId);
}

export function compareCollectionItems(
  a: CollectionItem,
  b: CollectionItem,
  sort: CollectionUiSort,
): number {
  switch (sort) {
    case "collector_asc":
      return compareCollectorThenStable(a, b);
    case "collector_desc": {
      const byNumber = compareCollectorNullsLast(
        a.release.collectorNumber,
        b.release.collectorNumber,
        true,
      );
      if (byNumber !== 0) {
        return byNumber;
      }
      const byYear = b.release.year - a.release.year;
      if (byYear !== 0) {
        return byYear;
      }
      const byName = compareName(a.release.casting.name, b.release.casting.name);
      if (byName !== 0) {
        return byName;
      }
      return compareReleaseId(a.releaseId, b.releaseId);
    }
    case "name_asc": {
      const byName = compareName(a.release.casting.name, b.release.casting.name);
      if (byName !== 0) {
        return byName;
      }
      return compareCollectorThenStable(a, b);
    }
    case "name_desc": {
      const byName = compareName(b.release.casting.name, a.release.casting.name);
      if (byName !== 0) {
        return byName;
      }
      return compareCollectorThenStable(a, b);
    }
    case "year_desc": {
      const byYear = b.release.year - a.release.year;
      if (byYear !== 0) {
        return byYear;
      }
      return compareCollectorThenStable(a, b);
    }
    case "year_asc": {
      const byYear = a.release.year - b.release.year;
      if (byYear !== 0) {
        return byYear;
      }
      return compareCollectorThenStable(a, b);
    }
    case "rare_first": {
      const byRare =
        rarityRankOf(a.release.huntType, a.release.isExclusive) -
        rarityRankOf(b.release.huntType, b.release.isExclusive);
      if (byRare !== 0) {
        return byRare;
      }
      return compareCollectorThenStable(a, b);
    }
    case "quantity_desc": {
      const byQty = b.quantity - a.quantity;
      if (byQty !== 0) {
        return byQty;
      }
      return compareCollectorThenStable(a, b);
    }
  }
}

export function sortCollectionItems(
  items: CollectionItem[],
  sort: CollectionUiSort,
): CollectionItem[] {
  return [...items].sort((a, b) => compareCollectionItems(a, b, sort));
}
